import { useEffect, useMemo, useState } from 'react'
import {
  attendanceLoadEmployeeRegimes,
  attendanceLoadWorkSchedules,
  attendanceSaveEmployeeRegimes,
  loadSchoolEmployees,
  type AttendanceWorkSchedule,
} from '../../lib/apiPhpAdapter'
import type { User } from '../../types'
import { formatEmployeeFullName } from '../../utils/employeeDisplay'
import { comparePersonNames } from '../../utils/personNameSort'
import './AttendanceOrgAdmin.css'

type Assignment = {
  work_schedule_id: number | null
}

function employeeSchoolId(e: User): number | null {
  if (e.school_employee_id != null) return Number(e.school_employee_id)
  const id = String(e.id || '')
  const m = id.match(/^se-(\d+)$/)
  if (m) return Number(m[1])
  return null
}

function sameAssign(a: Assignment | undefined, b: Assignment | undefined): boolean {
  const aa = a ?? { work_schedule_id: null }
  const bb = b ?? { work_schedule_id: null }
  return aa.work_schedule_id === bb.work_schedule_id
}

export function AttendanceRegimesAdminPanel({
  showAlert,
}: {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}) {
  const [employees, setEmployees] = useState<User[]>([])
  const [schedules, setSchedules] = useState<AttendanceWorkSchedule[]>([])
  const [assignments, setAssignments] = useState<Map<number, Assignment>>(() => new Map())
  const [savedAssignments, setSavedAssignments] = useState<Map<number, Assignment>>(() => new Map())
  const [loading, setLoading] = useState(true)
  const [savingAssign, setSavingAssign] = useState(false)
  const [empSearch, setEmpSearch] = useState('')
  /** all | none | id rozvrhu */
  const [filterSchedule, setFilterSchedule] = useState<string>('all')
  /** all | assigned | unassigned | dirty */
  const [filterStatus, setFilterStatus] = useState<'all' | 'assigned' | 'unassigned' | 'dirty'>('all')

  const reload = async () => {
    const [emps, assigns, sch] = await Promise.all([
      loadSchoolEmployees(),
      attendanceLoadEmployeeRegimes(),
      attendanceLoadWorkSchedules(),
    ])
    setEmployees(Array.isArray(emps) ? emps : [])
    setSchedules(Array.isArray(sch) ? sch.filter((s) => !!s.active) : [])
    const map = new Map<number, Assignment>()
    for (const a of assigns) {
      map.set(Number(a.school_employee_id), {
        work_schedule_id: a.work_schedule_id != null ? Number(a.work_schedule_id) : null,
      })
    }
    setAssignments(new Map(map))
    setSavedAssignments(new Map(map))
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        await reload()
      } catch (e: unknown) {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst přiřazení', 'error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAlert])

  const employeesWithPn = useMemo(() => {
    const list = employees.filter((e) => {
      const sid = employeeSchoolId(e)
      return sid != null && String(e.personal_number || '').trim() !== ''
    })
    list.sort(comparePersonNames)
    const q = empSearch.trim().toLowerCase()
    return list.filter((e) => {
      const sid = employeeSchoolId(e)!
      const value = assignments.get(sid) ?? { work_schedule_id: null }
      const assigned = value.work_schedule_id != null
      const dirty = !sameAssign(value, savedAssignments.get(sid))

      if (q) {
        const hit =
          e.name.toLowerCase().includes(q) ||
          (e.email || '').toLowerCase().includes(q) ||
          String(e.personal_number || '')
            .toLowerCase()
            .includes(q)
        if (!hit) return false
      }

      if (filterSchedule === 'none') {
        if (assigned) return false
      } else if (filterSchedule !== 'all') {
        if (value.work_schedule_id !== Number(filterSchedule)) return false
      }

      if (filterStatus === 'assigned' && !assigned) return false
      if (filterStatus === 'unassigned' && assigned) return false
      if (filterStatus === 'dirty' && !dirty) return false

      return true
    })
  }, [employees, empSearch, assignments, savedAssignments, filterSchedule, filterStatus])

  const dirtyCount = useMemo(() => {
    let n = 0
    const keys = new Set([...assignments.keys(), ...savedAssignments.keys()])
    for (const k of keys) {
      if (!sameAssign(assignments.get(k), savedAssignments.get(k))) n++
    }
    return n
  }, [assignments, savedAssignments])

  const setAssign = (sid: number, work_schedule_id: number | null) => {
    setAssignments((prev) => {
      const next = new Map(prev)
      next.set(sid, { work_schedule_id })
      return next
    })
  }

  return (
    <section className="attendance-panel att-org">
      <h2>Přiřazení na pozici</h2>
      <p className="attendance-muted">
        Každému zaměstnanci přiřaďte <strong>pozici</strong> (rozvrh směn Po–Ne). Prázdné roční
        nároky DOV/SAM/nemoc se při uložení doplní ze šablony pozice. Státní svátky plán nevyplní.
      </p>
      {loading ? <p>Načítání…</p> : null}

      <div className="att-org__regime-assign">
        <div className="att-org__box-head" style={{ marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Přiřazení zaměstnancům</h3>
          <button
            type="button"
            className="att-org__btn att-org__btn--primary"
            disabled={savingAssign || dirtyCount === 0}
            onClick={async () => {
              setSavingAssign(true)
              try {
                const payload: Array<{
                  school_employee_id: number
                  shift_id: null
                  work_schedule_id: number | null
                }> = []
                const keys = new Set([...assignments.keys(), ...savedAssignments.keys()])
                for (const k of keys) {
                  const a = assignments.get(k) ?? { work_schedule_id: null }
                  const b = savedAssignments.get(k)
                  if (!sameAssign(a, b)) {
                    payload.push({
                      school_employee_id: k,
                      shift_id: null,
                      work_schedule_id: a.work_schedule_id,
                    })
                  }
                }
                await attendanceSaveEmployeeRegimes(payload)
                await reload()
                showAlert(`Uloženo přiřazení (${payload.length})`, 'success')
              } catch (e: unknown) {
                showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
              } finally {
                setSavingAssign(false)
              }
            }}
          >
            {savingAssign
              ? 'Ukládám…'
              : dirtyCount > 0
                ? `Uložit změny (${dirtyCount})`
                : 'Uloženo'}
          </button>
        </div>
        <div className="att-org__filters">
          <input
            type="search"
            placeholder="Hledat jméno / e-mail / os. číslo…"
            value={empSearch}
            onChange={(e) => setEmpSearch(e.target.value)}
          />
          <label>
            Pozice
            <select value={filterSchedule} onChange={(e) => setFilterSchedule(e.target.value)}>
              <option value="all">Všechny</option>
              <option value="none">Bez pozice</option>
              {schedules.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                  {s.week_pattern === 'odd_even' ? ' (lichý/sudý)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            Stav
            <select
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus(e.target.value as 'all' | 'assigned' | 'unassigned' | 'dirty')
              }
            >
              <option value="all">Všichni</option>
              <option value="assigned">S pozicí</option>
              <option value="unassigned">Bez pozice</option>
              <option value="dirty">Neuložené změny</option>
            </select>
          </label>
          <button
            type="button"
            className="att-org__btn"
            onClick={() => {
              setEmpSearch('')
              setFilterSchedule('all')
              setFilterStatus('all')
            }}
          >
            Reset filtrů
          </button>
          <span className="attendance-muted att-org__filter-count">
            Zobrazeno {employeesWithPn.length}
          </span>
        </div>
        <div className="att-org__assign-scroll">
          <table className="att-org__table">
            <thead>
              <tr>
                <th>Zaměstnanec</th>
                <th>Os. číslo</th>
                <th>Pozice</th>
              </tr>
            </thead>
            <tbody>
              {employeesWithPn.map((e) => {
                const sid = employeeSchoolId(e)!
                const value = assignments.get(sid) ?? { work_schedule_id: null }
                const dirty = !sameAssign(value, savedAssignments.get(sid))
                return (
                  <tr key={sid} className={dirty ? 'att-org__row--dirty' : undefined}>
                    <td>
                      <strong>{formatEmployeeFullName(e)}</strong>
                      {e.email ? <div className="att-org__person-meta">{e.email}</div> : null}
                    </td>
                    <td>{e.personal_number || '—'}</td>
                    <td>
                      <select
                        value={value.work_schedule_id == null ? '' : String(value.work_schedule_id)}
                        onChange={(ev) => {
                          const v = ev.target.value
                          setAssign(sid, v === '' ? null : Number(v))
                        }}
                      >
                        <option value="">— bez pozice —</option>
                        {schedules.map((s) => (
                          <option key={s.id} value={String(s.id)}>
                            {s.name}
                            {s.week_pattern === 'odd_even' ? ' (lichý/sudý)' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                )
              })}
              {employeesWithPn.length === 0 && !loading ? (
                <tr>
                  <td colSpan={3} className="att-org__empty">
                    Žádný zaměstnanec neodpovídá filtrům.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}
